import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider } from "./contexts/AuthContext";
import { FontProvider, useFonts } from "./contexts/FontContext";
import { ProfileUpdateProvider } from "./contexts/ProfileUpdateContext";
import FontLoader from "./components/FontLoader";
import ErrorBoundary from "./components/ErrorBoundary";
import Logo from "./components/Logo";

// Lazy load pages for better performance
const Homepage = lazy(() => import("./components/Homepage"));
const CategoryPage = lazy(
  () => import("./components/CategoryPage"),
);
const ArticlePage = lazy(
  () => import("./components/ArticlePage"),
);
const AboutPage = lazy(() => import("./components/AboutPage"));
const ContactPage = lazy(
  () => import("./components/ContactPage"),
);
const AuthorPage = lazy(
  () => import("./components/AuthorPage"),
);
const BooksPage = lazy(() => import("./components/BooksPage"));
const AdminPage = lazy(
  () => import("./components/admin/AdminPage"),
);
const ClientDiagnostics = lazy(
  () => import("./components/dev/ClientDiagnostics"),
);

// Loading component for suspense fallback
const PageLoader = () => (
  <div className="min-h-screen bg-[#f6f3f0] flex items-center justify-center">
    <div className="text-center">
      <Logo
        size="xl"
        variant="dark"
        clickable={false}
        aria-label="ItinluX logo"
      />
    </div>
  </div>
);

function AppContent() {
  const { fontRefreshTrigger } = useFonts();

  return (
    <>
      <ErrorBoundary
        fallback={<div style={{ display: "none" }} />}
        onError={(error) => {
          console.warn(
            "FontLoader failed, continuing with default fonts:",
            error.message,
          );
        }}
      >
        <FontLoader refreshTrigger={fontRefreshTrigger} />
      </ErrorBoundary>
      <div className="min-h-screen bg-[#f6f3f0]">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Homepage />} />
            <Route
              path="/category/:categorySlug"
              element={<CategoryPage />}
            />
            <Route
              path="/article/:articleSlug"
              element={<ArticlePage />}
            />
            <Route
              path="/author/:authorSlug"
              element={<AuthorPage />}
            />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/books" element={<BooksPage />} />
            <Route path="/admin" element={<AdminPage />} />
            {/* Handle preview_page.html and other unknown routes */}
            <Route
              path="/preview_page.html"
              element={<Navigate to="/" replace />}
            />
            <Route
              path="*"
              element={<Navigate to="/" replace />}
            />
          </Routes>
          <ClientDiagnostics />
        </Suspense>
      </div>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <FontProvider>
        <ProfileUpdateProvider>
          <Router>
            <AppContent />
          </Router>
        </ProfileUpdateProvider>
      </FontProvider>
    </AuthProvider>
  );
}